import React, { useEffect, useState } from "react";
import { api, formatApiError } from "@/api";
import { PageHeader } from "@/pages/admin/AdminDashboard";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogTrigger, DialogFooter } from "@/components/ui/dialog";
import { toast } from "sonner";
import { Plus, AlertCircle } from "lucide-react";

const TYPE_LABELS = { conge: "Congé", conge_paye: "Congé payé", absence_maladie: "Absence maladie" };
const STATUS = { pending: ["En attente", "status-pending"], approved: ["Accepté", "status-active"], rejected: ["Refusé", "status-blocked"] };

export default function SalarieConges() {
  const [items, setItems] = useState([]);
  const [open, setOpen] = useState(false);
  const [absenceOpen, setAbsenceOpen] = useState(false);
  const [form, setForm] = useState({ type: "conge_paye", date_debut: "", date_fin: "", motif: "" });
  const [absence, setAbsence] = useState({ date: "", motif: "" });

  const load = async () => {
    try { const { data } = await api.get("/salarie/conges"); setItems(data); }
    catch (e) { toast.error(formatApiError(e.response?.data?.detail)); }
  };
  useEffect(() => { load(); }, []);

  const submit = async (e) => {
    e.preventDefault();
    try {
      await api.post("/salarie/conges", form);
      toast.success("Demande envoyée");
      setOpen(false);
      setForm({ type: "conge_paye", date_debut: "", date_fin: "", motif: "" });
      load();
    } catch (e) { toast.error(formatApiError(e.response?.data?.detail)); }
  };

  const submitAbsence = async (e) => {
    e.preventDefault();
    try {
      await api.post("/salarie/absence", absence);
      toast.success("Absence signalée");
      setAbsenceOpen(false);
      setAbsence({ date: "", motif: "" });
      load();
    } catch (e) { toast.error(formatApiError(e.response?.data?.detail)); }
  };

  return (
    <div className="min-h-full">
      <PageHeader eyebrow="Congés" title="Mes congés" sub="Demandez un congé ou signalez une absence. Vos demandes sont validées par votre chef."
        right={
          <div className="flex gap-2">
            <Dialog open={absenceOpen} onOpenChange={setAbsenceOpen}>
              <DialogTrigger asChild>
                <Button data-testid="salarie-signal-absence-btn" variant="outline" className="h-11 border-[#E60000] text-[#E60000] hover:bg-[#E60000] hover:text-white font-semibold">
                  <AlertCircle className="h-4 w-4 mr-2" /> Signaler une absence
                </Button>
              </DialogTrigger>
              <DialogContent>
                <DialogHeader><DialogTitle className="font-display text-2xl font-black tracking-tight">Signaler une absence</DialogTitle></DialogHeader>
                <form onSubmit={submitAbsence} className="space-y-4" data-testid="absence-form">
                  <div className="space-y-2">
                    <Label className="text-xs uppercase tracking-[0.2em] font-semibold">Date</Label>
                    <Input type="date" value={absence.date} onChange={(e) => setAbsence({ ...absence, date: e.target.value })} required data-testid="absence-date" className="h-11" />
                  </div>
                  <div className="space-y-2">
                    <Label className="text-xs uppercase tracking-[0.2em] font-semibold">Motif</Label>
                    <Textarea value={absence.motif} onChange={(e) => setAbsence({ ...absence, motif: e.target.value })} required data-testid="absence-motif" rows={3} />
                  </div>
                  <DialogFooter>
                    <Button type="submit" data-testid="absence-submit" className="bg-[#E60000] hover:bg-[#B91C1C] text-white">Signaler</Button>
                  </DialogFooter>
                </form>
              </DialogContent>
            </Dialog>
            <Dialog open={open} onOpenChange={setOpen}>
              <DialogTrigger asChild>
                <Button data-testid="salarie-request-conge-btn" className="h-11 bg-[#002FA7] hover:bg-[#002175] text-white font-semibold">
                  <Plus className="h-4 w-4 mr-2" /> Demander un congé
                </Button>
              </DialogTrigger>
              <DialogContent>
                <DialogHeader><DialogTitle className="font-display text-2xl font-black tracking-tight">Nouvelle demande de congé</DialogTitle></DialogHeader>
                <form onSubmit={submit} className="space-y-4" data-testid="conge-form">
                  <div className="space-y-2">
                    <Label className="text-xs uppercase tracking-[0.2em] font-semibold">Type</Label>
                    <Select value={form.type} onValueChange={(v) => setForm({ ...form, type: v })}>
                      <SelectTrigger data-testid="conge-type" className="h-11"><SelectValue /></SelectTrigger>
                      <SelectContent>
                        <SelectItem value="conge_paye">Congé payé</SelectItem>
                        <SelectItem value="conge">Congé sans solde</SelectItem>
                      </SelectContent>
                    </Select>
                  </div>
                  <div className="grid grid-cols-2 gap-3">
                    <div className="space-y-2">
                      <Label className="text-xs uppercase tracking-[0.2em] font-semibold">Du</Label>
                      <Input type="date" value={form.date_debut} onChange={(e) => setForm({ ...form, date_debut: e.target.value })} required data-testid="conge-debut" className="h-11" />
                    </div>
                    <div className="space-y-2">
                      <Label className="text-xs uppercase tracking-[0.2em] font-semibold">Au</Label>
                      <Input type="date" value={form.date_fin} onChange={(e) => setForm({ ...form, date_fin: e.target.value })} required data-testid="conge-fin" className="h-11" />
                    </div>
                  </div>
                  <div className="space-y-2">
                    <Label className="text-xs uppercase tracking-[0.2em] font-semibold">Motif (optionnel)</Label>
                    <Textarea value={form.motif} onChange={(e) => setForm({ ...form, motif: e.target.value })} data-testid="conge-motif" rows={3} />
                  </div>
                  <DialogFooter>
                    <Button type="submit" data-testid="conge-submit" className="bg-[#002FA7] hover:bg-[#002175] text-white">Envoyer</Button>
                  </DialogFooter>
                </form>
              </DialogContent>
            </Dialog>
          </div>
        }
      />
      <div className="p-6 lg:p-10">
        <div className="bg-white border border-[#DEE2E6]" data-testid="salarie-conges-list">
          <div className="grid grid-cols-12 gap-4 p-4 text-xs uppercase tracking-[0.2em] font-semibold bg-[#F8F9FA] border-b border-[#DEE2E6]">
            <div className="col-span-3">Type</div>
            <div className="col-span-4">Période</div>
            <div className="col-span-3">Motif</div>
            <div className="col-span-2">Statut</div>
          </div>
          {items.length === 0 ? (
            <div className="p-8 text-center text-sm text-[#868E96]">Aucune demande.</div>
          ) : items.map((i) => {
            const [label, cls] = STATUS[i.status] || [i.status, "status-inactive"];
            return (
              <div key={i.id} className="grid grid-cols-12 gap-4 p-4 border-b border-[#DEE2E6] items-center text-sm" data-testid={`conge-row-${i.id}`}>
                <div className="col-span-3 font-semibold">{TYPE_LABELS[i.type] || i.type}</div>
                <div className="col-span-4 font-mono text-xs">{i.date_debut} → {i.date_fin}</div>
                <div className="col-span-3 text-[#495057] truncate">{i.motif || "—"}</div>
                <div className="col-span-2"><span className={`status-pill ${cls}`}>{label}</span></div>
              </div>
            );
          })}
        </div>
      </div>
    </div>
  );
}
